import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'
import { Button } from '@orisini/ui/components/button'
import { Card } from '@orisini/ui/components/card'
import { Input } from '@orisini/ui/components/input'
import { Label } from '@orisini/ui/components/label'
import { useConfirm } from '@orisini/ui/components/confirm'
import { ConnectAccount } from './ConnectAccount'
import { accountsApi, type Workspace } from '../lib/accounts'

/**
 * Tab Account: akun tujuan terbit (opsional), nama project, dan menghapus
 * project. Login ulang dan menghapus akun platform tetap di Orisini Agent —
 * di sanalah sesi browsernya tinggal.
 */
export function ConnectionTab({ workspace: w, onChanged }: { workspace: Workspace; onChanged: () => Promise<void> }) {
  const navigate = useNavigate()
  const confirm = useConfirm()
  const [name, setName] = useState(w.name)
  const [busy, setBusy] = useState<string | null>(null)
  const [memilih, setMemilih] = useState(false)
  const a = w.account

  const run = async (key: string, fn: () => Promise<unknown>, ok: string) => {
    setBusy(key)
    try {
      await fn()
      toast.success(ok)
      await onChanged()
      return true
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Something went wrong')
      return false
    } finally {
      setBusy(null)
    }
  }

  const lepasAkun = async () => {
    const yes = await confirm({
      title: `Disconnect ${a?.username ? `@${a.username}` : 'this account'}?`,
      description:
        'This project keeps its topics, brand and content, and new content stays in the Content Bank for you to download. The account stays signed in on Orisini Agent, and posts already in its queue are kept.',
      confirmLabel: 'Disconnect',
    })
    if (yes) await run('disconnect', () => accountsApi.disconnect(w.id), 'Account disconnected')
  }

  const hapus = async () => {
    const yes = await confirm({
      title: `Delete ${w.name}?`,
      description:
        'Its topics, brand, styles and content are deleted. A connected account stays signed in on Orisini Agent, and posts already in its queue are kept.',
      confirmLabel: 'Delete project',
      destructive: true,
    })
    if (!yes) return
    setBusy('remove')
    try {
      await accountsApi.remove(w.id)
      toast.success(`${w.name} deleted`)
      navigate('/projects', { replace: true })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't delete the project")
      setBusy(null)
    }
  }

  return (
    <div className="space-y-6">
      {!w.productCode ? (
        <Card className="space-y-4 p-5">
          <div className="space-y-1">
            <p className="text-sm font-medium">No account connected</p>
            <p className="text-sm text-muted-foreground">
              Everything this project makes stays in the Content Bank, ready to download and post yourself. Connect a
              social account if you want Orisini Agent to post it for you.
            </p>
          </div>
          {memilih ? (
            <ConnectAccount
              busy={busy === 'connect'}
              onCancel={() => setMemilih(false)}
              onConnect={async (productCode, accountId) => {
                const ok = await run('connect', () => accountsApi.connect(w.id, productCode, accountId), 'Account connected')
                if (ok) setMemilih(false)
              }}
            />
          ) : (
            <Button onClick={() => setMemilih(true)}>Connect an account</Button>
          )}
        </Card>
      ) : (
        <>
          <Card className="divide-y divide-border">
            <Row label="Platform" value={w.platform ?? '—'} />
            <Row label="Username" value={a?.username ? `@${a.username}` : 'Not connected'} />
            <Row
              label="Session"
              value={
                !a
                  ? 'The account was removed from Orisini Agent'
                  : a.status === 'ok'
                    ? 'Signed in'
                    : a.status === 'needs_login'
                      ? 'Signed out — sign in again from Orisini Agent'
                      : (a.statusMessage ?? a.status)
              }
              hint={
                a?.lastSessionCheckAt
                  ? `Checked ${new Date(a.lastSessionCheckAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false })}`
                  : undefined
              }
            />
            <Row label="Orisini Agent" value={w.agentConnected ? 'Online' : 'Offline — open it on your computer to post'} />
          </Card>

          {a && (
            <Card className="flex flex-wrap items-center gap-4 p-4">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{a.paused ? 'Publishing is paused' : 'Publishing is on'}</p>
                <p className="text-xs text-muted-foreground">
                  {a.paused
                    ? 'Nothing is posted from this account until you resume. The queue keeps its order.'
                    : 'Pause to stop posting for a while without losing the queue.'}
                </p>
              </div>
              <Button
                variant="outline"
                disabled={!!busy}
                onClick={() => run('pause', () => accountsApi.setPaused(w.id, !a.paused), a.paused ? 'Publishing resumed' : 'Publishing paused')}
              >
                {busy === 'pause' && <Loader2 className="animate-spin" />}
                {a.paused ? 'Resume' : 'Pause'}
              </Button>
            </Card>
          )}

          <Card className="flex flex-wrap items-center gap-4 p-4">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">Disconnect account</p>
              <p className="text-xs text-muted-foreground">
                Keep the project and download its content instead. To sign out of {w.platform} or delete the account
                itself, use Orisini Agent.
              </p>
            </div>
            <Button variant="outline" disabled={!!busy} onClick={lepasAkun}>
              {busy === 'disconnect' && <Loader2 className="animate-spin" />}
              Disconnect
            </Button>
          </Card>
        </>
      )}

      <Card className="space-y-3 p-4">
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault()
            void run('rename', () => accountsApi.rename(w.id, name), 'Name saved')
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="ws-rename">Project name</Label>
            <Input id="ws-rename" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />
          </div>
          <Button type="submit" size="sm" disabled={!!busy || !name.trim() || name.trim() === w.name}>
            {busy === 'rename' && <Loader2 className="animate-spin" />}
            Save name
          </Button>
        </form>
      </Card>

      <Card className="flex flex-wrap items-center gap-4 border-destructive/40 p-4">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">Delete project</p>
          <p className="text-xs text-muted-foreground">Removes its topics, brand, styles and content from Orisini.</p>
        </div>
        <Button variant="destructive" disabled={!!busy} onClick={hapus}>
          {busy === 'remove' && <Loader2 className="animate-spin" />}
          Delete
        </Button>
      </Card>
    </div>
  )
}

function Row({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2 px-4 py-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right">
        {value}
        {hint && <span className="block text-xs text-muted-foreground">{hint}</span>}
      </span>
    </div>
  )
}
